import type { LanguagesResponse } from '@erp/shared';
import { Languages as LanguagesIcon } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Card, Skeleton } from '../../components/ui/index.js';
import { LocaleFlag } from '../../components/ui/LocaleFlag.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';

/**
 * Settings → Languages. Pick which catalog languages the tenant uses for its
 * content (item/category names + descriptions) and the default (base-column)
 * language. Saved as the `i18n` setting; the language switcher + translation
 * editors read it via GET /i18n/languages.
 */
export function LanguagesPage(): JSX.Element {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation();
  const canWrite = usePermission('settings.write');
  const [data, setData] = useState<LanguagesResponse | null>(null);
  const [enabled, setEnabled] = useState<Set<string>>(new Set());
  const [def, setDef] = useState('en');
  const [saving, setSaving] = useState(false);
  const [savedNote, setSavedNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const hydrate = (d: LanguagesResponse): void => {
    setData(d); setEnabled(new Set(d.enabled)); setDef(d.default);
  };

  useEffect(() => {
    void api().get<LanguagesResponse>('/i18n/languages').then((r) => hydrate(r.data)).catch(() => undefined);
  }, []);

  const toggle = (code: string): void =>
    setEnabled((s) => {
      const n = new Set(s);
      if (n.has(code)) { if (n.size > 1) n.delete(code); } else n.add(code);
      return n;
    });

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setSaving(true); setError(null); setSavedNote(null);
    try {
      const list = [...enabled];
      const r = await api().put<LanguagesResponse>('/i18n/languages', {
        enabled: list,
        default: enabled.has(def) ? def : list[0],
      });
      hydrate(r.data);
      setSavedNote(t('languages.saved', { defaultValue: 'Languages updated.' }));
    } catch {
      setError(t('languages.error', { defaultValue: 'Could not save the languages.' }));
    } finally {
      setSaving(false);
    }
  };

  if (!data) {
    return <Card><Skeleton className="h-6 w-48" /><Skeleton className="mt-4 h-40 w-full" /></Card>;
  }

  return (
    <Card>
      <h2 className="mb-2 flex items-center gap-2 text-lg font-semibold">
        <LanguagesIcon className="h-5 w-5 text-fg-muted" aria-hidden />
        {t('languages.title', { defaultValue: 'Languages' })}
      </h2>
      <p className="mb-4 text-sm text-fg-muted">
        {t('languages.description', {
          defaultValue: 'Choose the languages your catalog content (item & category names and descriptions) can be entered and shown in. The default is the language stored on each record before any translation is added.',
        })}
      </p>

      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <div className="flex flex-col divide-y divide-border rounded-lg border border-border">
          {data.catalog.map((l) => {
            const on = enabled.has(l.code);
            return (
              <div key={l.code} className="flex items-center justify-between gap-4 px-4 py-3">
                <label className="flex min-w-0 items-center gap-3">
                  <input
                    type="checkbox"
                    className="h-5 w-5 flex-none accent-brand"
                    checked={on}
                    disabled={!canWrite || (on && enabled.size === 1)}
                    onChange={() => toggle(l.code)}
                    aria-label={l.name}
                  />
                  <LocaleFlag code={l.code} className="h-5 w-auto flex-none rounded-[2px] ring-1 ring-border/50" />
                  <span className="min-w-0">
                    <span className="text-sm font-semibold text-fg" dir={l.isRtl ? 'rtl' : 'ltr'}>{l.nativeName}</span>
                    <span className="ms-2 text-xs text-fg-muted">{l.name} · {l.code}{l.isRtl ? ' · RTL' : ''}</span>
                  </span>
                </label>
                <label className={`flex flex-none items-center gap-1.5 text-xs ${on ? 'text-fg-muted' : 'opacity-40'}`}>
                  <input
                    type="radio"
                    name="default-language"
                    className="accent-brand"
                    checked={def === l.code}
                    disabled={!canWrite || !on}
                    onChange={() => setDef(l.code)}
                  />
                  {t('languages.default', { defaultValue: 'Default' })}
                </label>
              </div>
            );
          })}
        </div>

        {error && <p className="text-sm text-danger" role="alert">{error}</p>}
        {savedNote && <p className="text-sm text-success" role="status">{savedNote}</p>}

        {canWrite && <div><Button type="submit" loading={saving}>{tc('actions.save')}</Button></div>}
      </form>
    </Card>
  );
}
